import { Mutation, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { enqueueSnackbar } from 'notistack';
import { useState } from 'react';
import type { UseMutationResult } from '@tanstack/react-query';
import UserProfileService from './userProfile.service';

type ActionType = 'resetPassword';

interface MutationAction {
  actionType: ActionType;
  payload: any;
}

const useUserProfileMutation = (): {
  executeUserProfileAction: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  isUpdating: Record<string, boolean>;
  setIsUpdating: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  executeUserProfileActionMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
} => {
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});

  const queryClient = useQueryClient();
  const userProfileService = UserProfileService();

  const actionMap: { [key in ActionType]: Function } = {
    resetPassword: userProfileService.resetPassword,
  };

  const executeMutation = async ({ actionType, payload }: MutationAction) => {
    const serviceMethod = actionMap[actionType];
    if (!serviceMethod) {
      enqueueSnackbar('Error processing your request', { variant: 'error' });
    } else {
      return serviceMethod(payload);
    }
  };

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    onMutate: ({ actionType }) => {
      setIsUpdating((prev) => ({ ...prev, [actionType]: true }));
    },

    onSuccess: (response: any, { actionType }) => {
      const messages: { [key in ActionType]: string } = {
        resetPassword: 'Password successfully reset',
      };
      if (response.status !== 200) {
        throw new Error('Failed to reset password');
      }

      const successMessage = messages[actionType] || 'Operation successful';
      const errorMessage = `Failed to reset password`;

      enqueueSnackbar(successMessage, { variant: 'success' });
    },
    onError: (error: any, { actionType }) => {
      const errorMessage = `Failed to reset password`;
      enqueueSnackbar(errorMessage, { variant: 'error' });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['all-users'] });
    },
  });

  return {
    executeUserProfileAction: mutation.mutate,
    isUpdating,
    setIsUpdating,
    executeUserProfileActionMutation: mutation,
  };
};

export default useUserProfileMutation;
